<template>
  <div>
    <div
      :class="[
        'mx-auto my-0 grid max-w-5xl  md:grid-cols-2 ',
        { 'mt-16': !uiStore.mobile },
      ]"
    >
      <VaCard :assistant="vaCardData" @removeVA="vaCardData = null" />
      <div class="px-6 pb-9 pt-6 md:px-16">
        <div v-if="!user" class="mx-auto my-0">
          <template v-if="checkoutStore.assistantsObj.count">
            <h1 class="mb-0 text-center text-4xl font-bold">Results!</h1>
            <p class="text-center text-xl font-semibold">
              <span class="text-vgorange">
                {{ checkoutStore.assistantsObj.count }}
                possible virtual assistant</span
              >
              match<span v-if="checkoutStore.assistantsObj.count - 3 > 1"
                >es</span
              >!
            </p>
            <p class="mt-3 text-center text-xl font-semibold">
              Here are a few profiles for you to preview. Sign up, without
              obligation, to get full access to the VA candidates we’ve found.
            </p>
            <div class="flex justify-center gap-7 pt-5">
              <div
                v-for="assistant in checkoutStore.assistantsObj.assistants.slice(
                  0,
                  3
                )"
                :key="assistant.name"
                class="preview relative rounded-md bg-white p-2 shadow-md"
                tabindex="0"
                @click="uiStore.mobile && showVACard(assistant)"
              >
                <img
                  class="relative inline h-20 w-20 rounded-full bg-white object-cover"
                  :src="getAvatar(assistant)"
                  lazy-src="/img/profile.png"
                  alt="Profile image"
                />
                <div
                  class="absolute bottom-2 right-2 rounded-full bg-white p-1"
                >
                  <fa-icon
                    class="text-vgmedturq border-vgteal rounded-full border bg-white p-1"
                    icon="fa-regular fa-magnifying-glass"
                  />
                </div>
                <div
                  v-if="!uiStore.mobile"
                  class="card border-vgorange absolute -right-10 -top-16 z-30 border bg-white text-center"
                  :class="[
                    'min-w-[200px] rounded-2xl bg-white   p-4',
                    assistant.isSpecial ? 'bg-vgocean-200' : '',
                    uiStore.mobile ? 'mx-4' : 'card mx-0 flex flex-col',
                  ]"
                >
                  <div class="pb-5">
                    <div class="flex flex-col">
                      <div class="relative">
                        <v-avatar size="112" class="mb-2 bg-white">
                          <v-img :src="getAvatar(assistant)"> </v-img>
                        </v-avatar>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col">
                    <p class="name-cut mb-2 self-center text-base font-bold">
                      {{ assistant.display_name }}
                    </p>
                    <div class="grid grid-flow-row justify-items-center"></div>
                    <p class="cut my-2 h-[9] text-sm">
                      {{ assistant.blurb }}
                    </p>
                    <p class="text-vgorange text-xs font-bold">
                      Sign up to get full access!
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-5 flex flex-wrap justify-center">
              <v-chip
                v-for="criterion in checkoutStore.selectedRoles"
                :key="criterion.id + criterion.value"
                class="bg-vgsoftblue m-1 px-7 py-3 text-black"
              >
                <span class="truncate">{{ criterion.value }}</span>
              </v-chip>
            </div>
            <img
              src="/img/threeStars.png"
              class="m-4 mx-auto"
              style="max-width: 3rem"
              alt
            />
          </template>
          <h2 class="text-vgorange text-center text-3xl font-bold">
            Why sign up?
          </h2>
          <p class="pt-5 text-center text-base font-bold">
            Access hundreds (and growing) qualified and vetted Virtual
            Assistants. Zero risk and no obligation to subscribe to our services
            after sign up.
          </p>
        </div>
      </div>
      <div>
        <v-form ref="form" class="p-6" @submit.prevent="submit">
          <div class="rounded-xl border-2 bg-white px-4 py-11 shadow-md">
            <template v-if="loginBrands.length > 0">
              <div class="flex justify-evenly pt-4">
                <img
                  v-for="brand in loginBrands"
                  :key="brand"
                  class="h-full"
                  :src="brandMap[brand]"
                  height="128"
                  width="128"
                />
              </div>
              <p class="p-5 pb-0 text-center md:text-left">
                Welcome Back! You can log into VG OnDemand or the Virtual Gurus
                Talentplace with the same account. Just enter your Virtual Gurus
                account details below.
              </p>
            </template>
            <p class="pb-0 text-center text-xl font-bold md:text-left">
              {{ title }}
            </p>
            <template v-if="login.stage === 'verify'">
              <p
                class="mb-4 pb-0 text-center text-base font-semibold md:text-left"
              >
                We've sent a confirmation code to {{ login.email }}. Please
                enter the code below to create your account:
              </p>
              <label class="text-sm">
                Confirmation Code
                <v-text-field
                  v-model="confirmationCode"
                  :color="vgTeal"
                  density="compact"
                  variant="outlined"
                >
                </v-text-field>
              </label>
            </template>
            <template v-else>
              <template v-if="login.stage === 'reset_password'">
                <div class="p-4">
                  We've sent a confirmation code to {{ login.email }}. Please
                  enter the code below to set your password:
                </div>
                <label class="text-sm">
                  Confirmation Code
                  <div class="flex gap-4">
                    <v-text-field
                      v-model="resetPasswordTokenInput"
                      :color="vgTeal"
                      variant="outlined"
                    >
                    </v-text-field>
                    <vg-btn :loading="loading" @click="forgotPassword(true)"
                      >Send new code</vg-btn
                    >
                  </div>
                </label>
              </template>
              <template v-if="login.stage === 'register'">
                <div class="my-5 flex flex-row p-0">
                  <v-icon
                    v-if="isIndividual"
                    size="20"
                    :color="vgNavy"
                    @click="isIndividual = !isIndividual"
                  >
                    mdi-checkbox-marked-outline
                  </v-icon>
                  <v-icon
                    v-else
                    size="20"
                    :color="vgNavy"
                    @click="isIndividual = !isIndividual"
                  >
                    mdi-checkbox-blank-outline
                  </v-icon>
                  <p class="text-vgnavy -mb-0.5 pl-1 text-base">
                    I don't represent a business.
                  </p>
                </div>
                <transition name="fade">
                  <v-text-field
                    v-if="!isIndividual"
                    v-model="registerForm.company"
                    :rules="companyRequiredRules"
                    variant="outlined"
                    density="compact"
                    :color="vgTeal"
                    maxlength="200"
                    label="Company"
                  >
                  </v-text-field>
                </transition>
                <v-text-field
                  v-model="registerForm.full_name"
                  :rules="requiredRules"
                  density="compact"
                  variant="outlined"
                  required
                  :color="vgTeal"
                  maxlength="200"
                  label="Full Name"
                >
                </v-text-field>
                <v-text-field
                  v-model="registerForm.phone_number"
                  label="Phone"
                  :rules="phoneRules"
                  required
                  density="compact"
                  variant="outlined"
                  :color="vgTeal"
                  maxlength="200"
                >
                </v-text-field>
              </template>

              <v-text-field
                v-if="login.stage === 'email'"
                v-model.trim="registerForm.email"
                label="E-mail"
                :rules="emailRules"
                required
                density="compact"
                variant="outlined"
                :color="vgTeal"
              ></v-text-field>
              <p
                v-else-if="
                  login.stage !== 'collect' && login.stage !== 'reset_password'
                "
                class="font-bold"
              >
                <v-text-field
                  v-model.trim="login.email"
                  label="E-mail"
                  required
                  density="compact"
                  variant="outlined"
                  readonly
                  :color="vgTeal"
                  :model-value="login.email"
                >
                </v-text-field>
              </p>

              <v-text-field
                v-if="
                  login.stage === 'reset_password' || login.stage === 'signin'
                "
                v-model="registerForm.password"
                label="Password"
                autocomplete="new-password"
                :rules="
                  login.stage !== 'signin' && login.stage !== 'collect'
                    ? passwordRules
                    : requiredRules
                "
                required
                density="compact"
                type="password"
                variant="outlined"
                :color="vgTeal"
              ></v-text-field>
              <template v-if="login.stage === 'reset_password'">
                <v-text-field
                  v-model="registerForm.confirm_password"
                  label="Confirm Password"
                  autocomplete="new-password"
                  :rules="confirmPasswordRules"
                  required
                  density="compact"
                  variant="outlined"
                  type="password"
                  :color="vgTeal"
                >
                </v-text-field>
              </template>
              <v-row
                v-if="
                  googleReady &&
                  !addressInputeDisable &&
                  (login.stage === 'register' ||
                    (login.stage === 'collect' && collect.includes('city')))
                "
              >
                <v-col class="p-0 px-3">
                  <label class="text-sm" for="google-address-input">
                    Please specify your location
                  </label>
                  <vue-google-autocomplete
                    id="google-address-input"
                    ref="addressInput"
                    :class="{ addressError: showAddressInputError }"
                    :label="'city'"
                    autocomplete="off"
                    classname="city-autocomplete"
                    placeholder="Location"
                    types="(cities)"
                    @blur="addressInputHasFocused = true"
                    @placechanged="cityPicked"
                    @no-results-found="googleLocationNotFound = true"
                    @keypress.enter.prevent
                  >
                  </vue-google-autocomplete>

                  <p
                    class="pl-2 pt-1 text-xs text-[rgb(176,0,32)]"
                    :style="{
                      visibility: showAddressInputError ? 'visible' : 'hidden',
                    }"
                  >
                    Select the city from the generated list once you start
                    typing
                  </p>
                </v-col>
              </v-row>
              <v-row
                v-if="
                  addressInputeDisable &&
                  (login.stage === 'register' ||
                    (login.stage === 'collect' && collect.includes('city')))
                "
              >
                <v-col class="p-0 px-3">
                  <label class="text-sm">
                    Please specify your location
                    <fa-icon
                      class="text-vgorange"
                      icon="fa-regular fa-asterisk"
                    />
                    <v-text-field
                      :model-value="displayLocation"
                      :color="vgTeal"
                      readonly
                      density="compact"
                      variant="outlined"
                      hint="Click on pen to edit location"
                    >
                      <template #append-inner>
                        <fa-icon
                          icon="fa-regular fa-pencil"
                          class="text-vgstone -mt-1 pl-2 text-xl"
                          @click="clearLocationData"
                        />
                      </template>
                    </v-text-field>
                  </label>
                </v-col>
              </v-row>
            </template>
            <div
              v-if="
                login.stage === 'register' ||
                login.stage === 'email' ||
                login.stage === 'reset_password'
              "
              class="px-0 text-center md:text-left"
            >
              <p class="p-1 text-sm">
                Your privacy is extremely important to us. Review our Privacy
                policy
                <a
                  :href="`${WEBSITE_URL}/legal/privacy-policy`"
                  class="font-bold"
                  target="_blank"
                >
                  here</a
                >. Review Terms of Service
                <a
                  :href="`${WEBSITE_URL}/legal/terms-of-service`"
                  class="font-bold"
                  target="_blank"
                >
                  here</a
                >.
              </p>
            </div>

            <vg-alert :model-value="alert" @onClose="alert = null"></vg-alert>
            <div class="flex flex-col gap-4">
              <vg-btn :loading="loading" type="submit">
                {{ submitLabel }}
              </vg-btn>
              <button
                v-if="login.stage === 'register'"
                class="text-vgocean font-semibold uppercase underline"
                @click.prevent="login.stage = 'email'"
              >
                Back
              </button>
              <button
                v-else-if="login.stage === 'email'"
                class="text-vgocean font-semibold uppercase underline"
                @click.prevent="handleGoBack"
              >
                Back
              </button>
            </div>
            <div v-if="login.stage === 'signin'" class="pt-2 text-center">
              <vg-btn
                vg-type="tertiary"
                :loading="loading"
                @click.prevent.stop="forgotPassword()"
              >
                Forgot your Password?
              </vg-btn>
            </div>
            <div
              v-if="
                login.stage === 'signin' || login.stage === 'reset_password'
              "
              class="pt-2 text-center"
            >
              <a
                href="javascript:void(0)"
                class="text-sm"
                @click="
                  userStore.setLoginEmail('');
                  email = '';
                  userStore.setLoginStage('email');

                  registerForm.password = '';
                  loginBrands = [];
                "
              >
                Use another account
              </a>
            </div>
          </div>
        </v-form>
      </div>
    </div>
    <div class="bg-white drop-shadow-lg">
      <v-carousel
        v-model="testimonialsModel"
        :height="uiStore.mobile ? 300 : 200"
        class="mx-auto my-0 px-6 md:max-w-5xl"
        hide-delimiters
        cycle
      >
        <v-carousel-item
          v-for="(testimonial, i) in testimonials"
          :key="testimonial + i"
          class="h-60"
        >
          <div class="flex h-full px-4">
            <div
              class="mx-auto my-0 flex max-w-5xl content-center gap-6 self-center md:px-16"
            >
              <v-avatar size="60" class="md:self-center">
                <v-img :src="testimonial.avatar || '/img/img-quote_avatar.png'">
                </v-img>
              </v-avatar>
              <v-icon
                :color="vgOrange"
                class="-ml-11 self-start md:-mt-10 md:self-center"
                size="large"
                >mdi-comment-quote</v-icon
              >

              <div class="flex flex-col">
                <p class="text-vgsoftpurple text-base italic">
                  {{ testimonials[testimonialsModel].text }}
                </p>
                <p class="text-base italic text-black">
                  {{ testimonials[testimonialsModel].name }}
                </p>
              </div>
            </div>
          </div>
        </v-carousel-item>
      </v-carousel>
    </div>
    <div class="mx-auto my-16 max-w-5xl px-4">
      <h1 class="pl-3">FAQs</h1>
      <div class="grid md:grid-cols-2 md:gap-20">
        <div>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="(qa, index) in QAS.slice(0, 5)"
              :key="index"
            >
              <v-expansion-panel-title class="px-1 py-0">
                <template #actions>
                  <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
                </template>
                <p class="text-vgocean m-0 ml-3 w-full text-left">
                  {{ qa.question }}
                </p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p class="text-vgstone text-sm">{{ qa.answer }}</p>
              </v-expansion-panel-text>
              <div class="border-vgsilver-400 mx-3 border"></div>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div>
          <v-expansion-panels>
            <v-expansion-panel v-for="(qa, index) in QAS.slice(5)" :key="index">
              <v-expansion-panel-title class="px-1 py-0">
                <template #actions>
                  <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
                </template>
                <p class="text-vgocean m-0 ml-3 w-full text-left">
                  {{ qa.question }}
                </p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p class="text-vgstone text-sm">{{ qa.answer }}</p>
              </v-expansion-panel-text>
              <div class="border-vgsilver-400 mx-3 border"></div>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  isValidEmail,
  isValidPhone,
  validatePassword,
} from '@/services/validationService';
import {
  register,
  verify,
  login,
  checkUserExistByEmail,
  sessionUserHasPermission,
  resetPassword,
  changePasswordByToken,
} from '@/services/clientSessionService';
import { getTimezone } from '@/services/userService';
import gmapsInit from '@/utils/gmaps';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import tailwind from 'tailwind.config';
import { WEBSITE_URL, API_URL } from '@/config';
import { mapStores, storeToRefs } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { useClientDashboardStore } from '@/stores/clientDashboard';
import { useConfigStore } from '@/stores/config';
import { useUiStore } from '@/stores/ui';
import { useUserStore } from '@/stores/user';
import { saveProject } from '@/services/projectService';
import VaCard from '@/components/VaCard.vue';
import { canGoBack } from '@/services/httpService';

export default {
  name: 'ClientSignUp',
  components: { VueGoogleAutocomplete, VaCard },
  data() {
    const userStore = useUserStore();
    const clientDashboardStore = useClientDashboardStore();
    const { user } = storeToRefs(userStore);
    const { login } = userStore;
    const { clientOrganizedProjects } = storeToRefs(clientDashboardStore);
    const { initClientProjects } = clientDashboardStore;

    return {
      userStore,
      user,
      login,
      clientOrganizedProjects,
      initClientProjects,
      loading: false,
      registerForm: {
        full_name: '',
        email: '',
        password: '',
        confirm_password: '',
        company: '',
        phone_number: '',
        country: '',
        state_prov: '',
        city: '',
      },
      collect: [],
      confirmationCode: '',
      resetPasswordToken: '',
      addressInputeDisable: false,
      googleReady: false,
      alert: null,
      loginBrands: [],
      WEBSITE_URL,
      perks: [
        {
          src: '/img/0309_VirtualGurus-StarIcon_Navy.jpg',
          title: 'Your team, your way:',
          description:
            'Assemble the team you need, whether it’s one assistant or an army.',
        },
        {
          src: '/img/0309_VirtualGurus-StarIcon_Navy.jpg',
          title: 'Fit, not frustration:',
          description: 'Work with assistants curated to match your needs.',
        },
        {
          src: '/img/0309_VirtualGurus-StarIcon_Navy.jpg',
          title: 'Plans for any budget:',
          description:
            'Buy the hours you need and adjust up or down when necessary.',
        },
        {
          src: '/img/0309_VirtualGurus-StarIcon_Navy.jpg',
          title: 'Be part of the solution:',
          description:
            'Provide fair opportunities for underrepresented people.',
        },
        {
          src: '/img/0309_VirtualGurus-StarIcon_Navy.jpg',
          title: 'Buy onshore:',
          description:
            'Work with Canadian and U.S. talent while benefiting the North American economy.',
        },
      ],
      testimonials: [
        {
          text: `They make it easy to get started and get who you need. My VA is a treasure and has put my research months ahead.`,
          name: `Sumner D, Founder & CEO, Tradesflow`,
          avatar: '/img/clients/Sumner D.jpg',
        },
        {
          text: `I've had exceptional support for growing my business. I highly recommend Virtual Gurus for startups and consulting organizations.`,
          name: `Dr. Lynn P, Founder, Deep Vivid`,
          avatar: '/img/clients/Lynn P.jpg',
        },
        {
          text: `We’re thrilled to support their mission to provide opportunities for diverse communities. It’s not just a placement approach; it’s a people partnership.`,
          name: `Carey H, Founder & Partner, 321 Growth Academy`,
          avatar: '/img/clients/Carey H.jpg',
        },
      ],
      QAS: [
        {
          question: 'When do I pay?',
          answer: `You will pay for your first month as soon as you sign up so our service can start matching you with the right VA.`,
        },
        {
          question: 'Do I need to pick my own VA?',
          answer: `We take the load off of you by doing the matching for you, with our large pool of talent we don’t expect clients to dig through instead our matching team will present curated choices to find the best fit.

`,
        },
        {
          question: 'Do I always have the same VA?',
          answer: `Yes, barring life events or other unforeseen circumstances the VA or VAswho are matched with you will be working with you for your time with us.

`,
        },
        {
          question: 'Does it cost more to have more than one VA?',
          answer: `No, your cost is for our service which can be built to meet your needs, one VA or more as is best for you.

`,
        },
        {
          question: 'Do my hours roll over?',
          answer: `No, to ensure that we are maintaining continuity of service for you and quality of life for our VAs you need to use your hours within the month, similar to a cell phone plan.

`,
        },
        {
          question: 'Do I need to train my VA?',
          answer: `Yes, while your VA will have the right skills you will need to provide training and guidance with your unique systems and needs. Most clients see full productivity from their VA within 4-6 weeks.

`,
        },
        {
          question: 'Do I need to provide software or hardware?',
          answer: `All VA’s already have their own computer. Most VA’s already have a basic software (office etc) suite however specific software needs or seats would need to be provided by yourself both for security and logistical purposes.

`,
        },
        {
          question: 'How do I know my time is being used?',
          answer: `Our custom software solution, Talentplace, provides a minute by minute breakdown of the work being done as well as weekly reports.

`,
        },
        {
          question: 'Do you have a referral program?',
          answer: `Yes, we love when our client’s recommend us to others, and to show our appreciation we provide incentives to our current clients of a discount on the next monthly bill or additional free hours. `,
        },
      ],
      testimonialsModel: 0,
      requiredRules: [(v) => !!v || 'Required'],
      passwordRules: [(v) => !!v || 'Required', validatePassword],
      confirmPasswordRules: [
        (v) => !!v || 'Required',
        (v) =>
          v === this.registerForm.password || 'Confirm Password must match',
      ],
      phoneRules: [
        (v) => !!v || 'Required',
        (v) => isValidPhone(v) || 'Phone must be valid',
      ],
      emailRules: [
        (v) => !!v || 'Required',
        (v) => isValidEmail(v) || 'E-mail must be valid',
      ],
      companyRequiredRules: [(v) => this.isIndividual || !!v || 'Required'],
      vgTeal: tailwind.theme.extend.colors.vgteal[500],
      vgOrange: tailwind.theme.extend.colors.vgorange[500],
      vgNavy: tailwind.theme.extend.colors.vgnavy[500],
      brandMap: {
        virtualgurus: '/img/Virtual-Gurus-Assistants-Color-Logo-mobile.png',
        askbetty: '/img/askbetty-logo.png',
        ondemand: '/img/VG OnDemand-logo-vertical.svg',
      },
      isIndividual: false,
      vgBlack: '#000000',
      vaCardData: null,
      addressInputHasFocused: false,
    };
  },
  computed: {
    ...mapStores(useUiStore, useCheckoutStore, useConfigStore),
    vaProfiles() {
      return this.user
        ? this.checkoutStore.assistantsObj.assistants
        : this.checkoutStore.assistantsObj.assistants?.slice(0, 3);
    },
    totalHours() {
      return this.checkoutStore.matchingCriteria.hours?.reduce(
        (result, current) => {
          return result + parseInt(current);
        },
        0
      );
    },
    title() {
      switch (this.login.stage) {
        case 'register':
          return 'Start your journey to reclaiming your time.';
        case 'collect':
          return this.$t('signIn.collect.message');
        case 'verify':
          return 'Confirm your email address';
        case 'reset_password':
          return this.$t('signIn.enterCodeSetPass.message');
        case 'signin':
          return 'Sign In';
        case 'email':
          return 'Please provide your email address';
        default:
          return '';
      }
    },

    submitLabel() {
      switch (this.login.stage) {
        case 'register':
          return 'Create Account';
        case 'collect':
          return 'Submit';
        case 'verify':
        case 'reset_password':
          return 'Confirm';
        case 'email':
          return 'Next';
        default:
          return 'Sign In';
      }
    },
    showAddressInputError: function () {
      return (
        (!this.registerForm.country &&
          !this.registerForm.state_prov &&
          !this.registerForm.city &&
          this.addressInputHasFocused) ||
        this.googleLocationNotFound
      );
    },
    displayLocation() {
      return `${this.registerForm.city}, ${this.registerForm.state_prov}, ${this.registerForm.country}`;
    },
    resetPasswordTokenInput: {
      get() {
        return this.resetPasswordToken;
      },
      set(value) {
        this.resetPasswordToken = value.toUpperCase();
      },
    },
  },
  watch: {
    async 'login.stage'(value) {
      if (value === 'register' || value === 'collect') {
        try {
          await gmapsInit();
          this.googleReady = true;
        } catch (error) {
          window.location.reload(true);
        }
      }
    },
    'checkoutStore.matchingCriteria': {
      handler: function (newVal) {
        if (newVal.rids && newVal.hours) {
          this.searchVAs({ matchAnyRole: true });
        }
      },
      immediate: true,
    },
  },
  async created() {
    if (this.login.stage === 'register' || this.login.stage === 'collect') {
      try {
        await gmapsInit();
        this.googleReady = true;
      } catch (error) {
        window.location.reload(true);
      }
    }
  },

  methods: {
    async forgotPassword(send_new_token) {
      this.loading = true;
      try {
        await resetPassword(this.login.email, send_new_token);
        this.login.stage = 'reset_password';
      } catch (error) {
        return this.$root.$snackbar.error(
          error?.data?.message ?? this.$t('signIn.resettingPassword.error')
        );
      } finally {
        this.loading = false;
      }

      if (send_new_token) {
        this.$root.$snackbar.message(
          `A new Confirmation Code has been sent to ${this.registerForm.email}.`
        );
      }
    },
    async submit() {
      this.alert = null;
      if (this.login.stage === 'email') {
        try {
          if (!isValidEmail(this.registerForm.email)) {
            this.alert = {
              show: true,
              message: this.$t('signIn.validEmail.error'),
            };
            return;
          }
          this.userStore.setLoginEmail(this.registerForm.email);

          const result = await checkUserExistByEmail(this.registerForm.email);
          const matches = result.matches;

          if (
            result.nextStage === 'reset_password' ||
            result.nextStage === 'verify' ||
            result.nextStage === 'register'
          ) {
            this.userStore.setLoginStage(result.nextStage);
            return;
          }

          if (
            matches.length === 1 &&
            matches[0].permissions.includes('askbetty_client') &&
            !matches[0].permissions.includes('client')
          ) {
            this.loginBrands = ['ondemand'];
          } else if (
            matches.length === 1 &&
            matches[0].permissions.includes('askbetty_client') &&
            matches[0].permissions.includes('client')
          ) {
            this.loginBrands = ['ondemand', 'virtualgurus'];
          }

          this.userStore.setLoginStage('signin');
        } catch (error) {
          this.alert = {
            show: true,
            message: this.$t('signIn.checkingEmail.error'),
          };
          return;
        }
        return;
      }

      if (!(await this.$refs.form.validate()).valid) {
        return;
      }

      if (this.login.stage === 'signin' || this.login.stage === 'collect') {
        this.handleLogin();
      } else if (this.login.stage === 'register') {
        this.handleRegister();
      } else if (this.login.stage === 'verify') {
        this.handleVerify();
      } else if (this.login.stage === 'reset_password') {
        this.handleSetNewPassword();
      }
    },
    async handleLogin() {
      this.loading = true;
      let collect;

      if (this.login.stage === 'collect') {
        collect = {};

        if (this.collect.includes('city')) {
          collect.country = this.registerForm.country;
          collect.state_prov = this.registerForm.state_prov;
          collect.city = this.registerForm.city;
          collect.latitude = this.registerForm.latitude;
          collect.longitude = this.registerForm.longitude;

          try {
            const { timezone, timezone_offset } = await getTimezone(
              this.registerForm.latitude,
              this.registerForm.longitude
            );

            collect.timezone = timezone;
            collect.timezone_offset = timezone_offset;
          } catch (error) {
            this.alert = {
              show: true,
              message: this.$t('signIn.updatingDetails.error'),
            };

            return;
          }
        }
      }

      try {
        const response = await login(
          this.login.email,
          this.registerForm.password,
          collect
        );

        this.userStore.saveSession({
          token: response.token,
          user: {
            ...response,
            token: undefined,
          },
        });
        await this.userStore.initUser();
        if (response.permissions.includes('client')) {
          this.$gtm?.trackEvent({
            event: 'DL - Client Portal Login',
          });
        }

        if (
          (this.login.stage === 'reset_password' ||
            this.login.stage === 'verify' ||
            this.login.stage === 'signin') &&
          sessionUserHasPermission('client')
        ) {
          await this.initClientProjects({ forceRefresh: true });
          if (this.clientOrganizedProjects.draft.projects.length !== 0) {
            let newestDraft =
              this.clientOrganizedProjects.draft.projects.find(
                (project) =>
                  project.start_date <
                  this.clientOrganizedProjects.draft.projects[0].start_date
              ) || this.clientOrganizedProjects.draft.projects[0];
            if (
              newestDraft.matching_criteria.rids &&
              newestDraft.matching_criteria.rids.length !== 0 &&
              !this.checkoutStore.matchingCriteria?.rids
            ) {
              await this.checkoutStore.updateMatchingCriteria(
                newestDraft.matching_criteria
              );
            } else if (
              !(
                newestDraft.matching_criteria.rids &&
                newestDraft.matching_criteria.rids.length !== 0
              )
            ) {
              await this.checkoutStore.updateMatchingCriteria({
                ...this.checkoutStore.matchingCriteria,
                uuid: newestDraft.uuid,
              });
            } else if (
              newestDraft.matching_criteria.rids &&
              newestDraft.matching_criteria.rids.length !== 0 &&
              this.checkoutStore.matchingCriteria?.rids &&
              this.checkoutStore.matchingCriteria?.rids?.length !== 0
            ) {
              const projectUUID = await saveProject().then(
                (response) => response.project_uuid
              );

              await this.checkoutStore.updateMatchingCriteria({
                ...this.checkoutStore.matchingCriteria,
                uuid: projectUUID,
              });
            }
          } else {
            const projectUUID = await saveProject().then(
              (response) => response.project_uuid
            );

            await this.checkoutStore.updateMatchingCriteria({
              ...this.checkoutStore.matchingCriteria,
              uuid: projectUUID,
            });
          }
          this.checkoutStore.initialSetup();
          await this.initClientProjects({ forceRefresh: true });
          return this.$router.push(`/search`);
        }
      } catch (error) {
        if (error.status === 400 && error.collect) {
          this.login.stage = 'collect';
          this.collect = error.collect;

          return;
        }
        if (this.login.stage !== 'signin') {
          this.userStore.setLoginStage('signin');

          return;
        }

        this.alert = {
          show: true,
          message: `Sign In failed`,
        };
        return;
      } finally {
        this.loading = false;
      }
    },
    async handleRegister() {
      if (!(this.registerForm.latitude && this.registerForm.longitude)) {
        this.addressInputHasFocused = true;
        return;
      }

      this.loading = true;

      try {
        const { timezone, timezone_offset } = await getTimezone(
          this.registerForm.latitude,
          this.registerForm.longitude
        );

        await register({
          ...this.registerForm,
          email: this.login.email,
          timezone,
          timezone_offset,
          is_individual: this.isIndividual,
        });
        this.userStore.setLoginStage('reset_password');
        this.$refs.form.resetValidation();
      } catch (error) {
        if (
          error?.data?.message ===
          `Email ${this.login.email} is already registered.`
        ) {
          this.userStore.setLoginStage('signin');
        }
        this.alert = {
          show: true,
          message: `Unable to register - ${
            error?.data?.message || 'please try again later'
          }`,
        };
        return;
      } finally {
        this.loading = false;
      }
    },
    async handleVerify() {
      if (this.confirmationCode.length !== 6) {
        this.alert = {
          show: true,
          message: `Confirmation Code should be 6 characters`,
        };
      }
      this.loading = true;
      try {
        await verify(this.confirmationCode, this.login.email);

        if (this.registerForm.password) {
          this.handleLogin();
        } else {
          this.userStore.setLoginStage('signin');
        }
      } catch (error) {
        this.alert = {
          show: true,
          message: `Unable to verify your email at this time. Please try again later.`,
        };
        return;
      } finally {
        this.loading = false;
      }
    },
    cityPicked(location, place) {
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'administrative_area_level_1': {
            this.registerForm.state_prov = component.long_name;
            break;
          }
          case 'country': {
            this.registerForm.country = component.long_name;
            break;
          }
          case 'locality': {
            this.registerForm.city = component.long_name;
            break;
          }
        }
      }

      this.registerForm.latitude = location.latitude;
      this.registerForm.longitude = location.longitude;
      this.googleLocationNotFound = false;
      this.addressInputeDisable = true;
    },

    clearLocationData() {
      this.registerForm.country = '';
      this.registerForm.state_prov = '';
      this.registerForm.city = '';
      this.registerForm.latitude = '';
      this.registerForm.longitude = '';
      this.addressInputeDisable = false;
    },
    getAvatar(assistant) {
      if (assistant.avatar_id === -1) {
        return '/img/profile.png';
      }
      return `${API_URL}/file/avatar/${assistant.avatar_id}`;
    },
    searchVAs() {
      this.checkoutStore.searchVAs({
        matchAnyRole: true,
        ...this.checkoutStore.matchingCriteria,
      });
    },
    showVACard(assistant) {
      this.vaCardData = assistant;
    },
    handleGoBack() {
      if (canGoBack() && this.$route.meta.fromFullPath !== '/') {
        this.$router.go(-1);
      } else {
        this.$router.push(`/?step=hours`);
      }
    },
    async handleSetNewPassword() {
      try {
        await changePasswordByToken(
          this.login.email,
          this.resetPasswordToken,
          this.registerForm.password
        );

        this.$root.$snackbar.message('Password successfully changed.');
        this.handleLogin();
      } catch (error) {
        return this.$root.$snackbar.error(
          error?.data?.message ?? this.$t('signIn.changingPassword.error')
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

* :deep(.v-expansion-panel .v-expansion-panel__shadow),
* :deep(.v-expansion-panels--variant-default > .v-expansion-panel::after) {
  @apply hidden;
}
* :deep(.v-expansion-panel-title__overlay) {
  @apply shadow-none opacity-0 hidden;
}
* :deep(.v-theme--light.v-expansion-panels .v-expansion-panel) {
  background-color: rgba(0, 0, 0, 0);
}
*
  :deep(.v-expansion-panel--active
    > .v-expansion-panel-title--active
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  -webkit-transform: rotate(-180deg);
  transform: rotate(-180deg);
}
*
  :deep(.v-expansion-panel
    > .v-expansion-panel-title
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  @apply transition-transform;
}
* :deep(.v-text-field > .v-input__control fieldset) {
  padding-left: 15px;
}

* :deep(.v-window__next) {
  right: 0;
}

.preview:hover .card {
  display: block;
  animation: scale 0.2s both;
}
@keyframes scale {
  from {
    transform: scale(0.4);
  }
  to {
    transform: scale(1);
  }
}

.card {
  display: none;
}
</style>
